import { Stack, Tooltip } from "@mui/material";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  Chip,
  EmptyState,
  IconButton,
  Table,
  TableHeader,
  TableProps,
  TableRowItem,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { RotateCw, SearchX, Trash2 } from "lucide-react";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { StyledEllipsisTypography } from "../../../components/StyledEllipsisTypography";
import { convertTimestampFormat } from "../../utils/DateUtils";
import { APIKeyModel } from "../models/APIKeyModel";
import { API_KEYS_PER_PAGE } from "../stores/APIKeyManagementStore";
import { useAPIKeyManagementStore } from "../stores/Hooks";

const KEY_ID_COLUMN_WIDTH = "400px";
const PROJECTS_COLUMN_WIDTH = "300px";
const STATUS_COLUMN_WIDTH = "120px";
const CREATED_AT_COLUMN_WIDTH = "220px";
const EXPIRES_AT_COLUMN_WIDTH = "220px";
const ACTION_COLUMN_WIDTH = "128px";
const TABLE_WIDTH = `calc(${KEY_ID_COLUMN_WIDTH} + ${PROJECTS_COLUMN_WIDTH} + ${STATUS_COLUMN_WIDTH} + ${CREATED_AT_COLUMN_WIDTH} + ${EXPIRES_AT_COLUMN_WIDTH} + ${ACTION_COLUMN_WIDTH})`;

enum APIKeysTableColumn {
  KeyId = "KEY_ID",
  Projects = "PROJECTS",
  Status = "STATUS",
  CreatedAt = "CREATED_AT",
  ExpiresAt = "EXPIRES_AT",
  Actions = "ACTIONS",
}

interface APIKeysTableProps {
  onEditAssignments: (apiKey: Instance<typeof APIKeyModel>) => void;
  onRotate: (apiKey: Instance<typeof APIKeyModel>) => void;
  onDelete: (apiKey: Instance<typeof APIKeyModel>) => void;
}

export const APIKeysTable: React.FC<APIKeysTableProps> = observer(
  ({ onEditAssignments, onRotate, onDelete }) => {
    const { t } = useTranslation();
    const spacing = useSpacing();
    const typography = useTypography();
    const store = useAPIKeyManagementStore();
    const isInitialLoad = useRef(true);

    const headers: TableHeader = [
      {
        columnId: APIKeysTableColumn.KeyId,
        label: t("externalPublicApis.apiKeyManagementKeyIdColumn"),
        width: KEY_ID_COLUMN_WIDTH,
      },
      {
        columnId: APIKeysTableColumn.Status,
        label: t("externalPublicApis.apiKeyManagementStatusColumn"),
        width: STATUS_COLUMN_WIDTH,
      },
      {
        columnId: APIKeysTableColumn.ExpiresAt,
        label: t("externalPublicApis.apiKeyManagementExpiresAtColumn"),
        width: EXPIRES_AT_COLUMN_WIDTH,
      },
      {
        columnId: APIKeysTableColumn.Projects,
        label: t("externalPublicApis.apiKeyManagementProjectsColumn"),
        width: PROJECTS_COLUMN_WIDTH,
      },
      {
        columnId: APIKeysTableColumn.CreatedAt,
        label: t("externalPublicApis.apiKeyManagementCreatedAtColumn"),
        width: CREATED_AT_COLUMN_WIDTH,
      },
      {
        columnId: APIKeysTableColumn.Actions,
        label: EMPTY_CHARACTER,
        width: ACTION_COLUMN_WIDTH,
      },
    ];

    const getProjectNames = (apiKey: Instance<typeof APIKeyModel>): string => {
      if (apiKey.isAdmin) {
        return t("externalPublicApis.apiKeyManagementAllProjects");
      }
      return apiKey.projectIds
        .map((projectId) => {
          const project = store.activeProjects.find(
            (activeProject) => activeProject.projectId === projectId,
          );
          return (
            project?.projectName ??
            t("externalPublicApis.apiKeyManagementUnavailableProject", {
              projectId,
            })
          );
        })
        .join(", ");
    };

    const getCells = (
      apiKey: Instance<typeof APIKeyModel>,
    ): TableRowItem["cells"] => [
      {
        columnId: APIKeysTableColumn.KeyId,
        data: (
          <StyledEllipsisTypography
            data={apiKey.apiKeyId}
            typography={typography}
            t={t}
          />
        ),
      },
      {
        columnId: APIKeysTableColumn.Status,
        data: (
          <Stack alignItems="start">
            <Chip
              label={t(
                apiKey.status === "ACTIVE"
                  ? "externalPublicApis.apiKeyManagementActiveStatus"
                  : "externalPublicApis.apiKeyManagementExpiredStatus",
              )}
              color={apiKey.status === "ACTIVE" ? "greenSubtle" : "redSubtle"}
            />
          </Stack>
        ),
      },
      {
        columnId: APIKeysTableColumn.ExpiresAt,
        data: convertTimestampFormat(apiKey.expiresAt),
      },
      {
        columnId: APIKeysTableColumn.Projects,
        data: (
          <StyledEllipsisTypography
            data={getProjectNames(apiKey)}
            typography={typography}
            t={t}
          />
        ),
      },
      {
        columnId: APIKeysTableColumn.CreatedAt,
        data: convertTimestampFormat(apiKey.createdAt),
      },
      {
        columnId: APIKeysTableColumn.Actions,
        data: (
          <Stack direction="row" justifyContent="end" width="100%">
            <Tooltip title={t("externalPublicApis.apiKeyManagementRotateKey")}>
              <div onClick={(event) => event.stopPropagation()}>
                <IconButton
                  name="rotateAPIKey"
                  size="small"
                  icon={<RotateCw />}
                  variant="plain-neutral"
                  hoverColor="buttonOnSurfaceIcon"
                  onClick={() => onRotate(apiKey)}
                />
              </div>
            </Tooltip>
            <Tooltip title={t("externalPublicApis.apiKeyManagementDeleteKey")}>
              <div onClick={(event) => event.stopPropagation()}>
                <IconButton
                  name="deleteAPIKey"
                  size="small"
                  icon={<Trash2 />}
                  variant="plain-color"
                  color="destructive"
                  onClick={() => onDelete(apiKey)}
                />
              </div>
            </Tooltip>
          </Stack>
        ),
      },
    ];

    const paginationOption: TableProps["paginationOption"] = {
      itemsPerPage: API_KEYS_PER_PAGE,
      initialPageNumber: store.currentPageIndex,
      getPageIndicatorText: (startItem, endItem, totalItems): string =>
        t("common.paginationIndicationText", {
          startItem,
          endItem,
          totalItems,
        }),
    };

    const onTableOptionsChange: TableProps["onTableOptionsChange"] = async (
      options,
      setTotalItems,
    ) => {
      const pageIndex = options.pageNumber ?? 1;
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
      } else {
        await store.getAPIKeys(pageIndex, true);
      }
      setTotalItems(store.totalItems);
      return store.apiKeys.map((apiKey) => ({
        id: apiKey.apiKeyId,
        cells: getCells(apiKey),
      }));
    };

    return (
      <Stack gap={spacing.spaceSM} width="100%" maxWidth={TABLE_WIDTH}>
        <Table
          name="apiKeysTable"
          header={headers}
          paginationOption={paginationOption}
          onTableOptionsChange={onTableOptionsChange}
          viewOverrides={{
            empty: {
              icon: (
                <EmptyState
                  icon={<SearchX />}
                  title={t(
                    "externalPublicApis.apiKeyManagementEmptyStateTitle",
                  )}
                  description={EMPTY_CHARACTER}
                  width="100%"
                />
              ),
              message: EMPTY_CHARACTER,
            },
            loading: {
              message: t("externalPublicApis.apiKeyManagementLoadingKeys"),
            },
          }}
          onRowClick={(row) => {
            const apiKey = store.apiKeys.find(
              ({ apiKeyId }) => apiKeyId === row.id,
            );
            if (apiKey && apiKey.status === "ACTIVE" && !apiKey.isAdmin) {
              onEditAssignments(apiKey);
            }
          }}
          leftPinnedColumnIds={[]}
          rightPinnedColumnIds={[APIKeysTableColumn.Actions]}
        />
      </Stack>
    );
  },
);
